import { createFileRoute } from "@tanstack/react-router"
import { ColumnSort, SortingState } from "@tanstack/react-table"
import { getPaginatedActivities } from "@/orval/gen"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { Page } from "@components/Page"
import { Stack, Group, MultiSelect, TextInput } from "@mantine/core"
import { useTranslation } from "react-i18next"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useActivityTableColumns } from "./-components/use-activity-table-columns"
import { InfiniteScrollingTable } from "@/routes/-components/custom-tanstack-table"
import { DateRangePickerInputWithSeasonPresets } from "@components/date-picker/DateRangePickerInputWithSeasonPresets"
import { DefaultInfiniteList } from "@components/InfiniteList"
import { ActivityListItemCard } from "../../-components/activity-list-item-card"
import { LocationTreeSelect } from "@components/tree-select/LocationTreeSelect"
import { ActivityStatusTreeSelect } from "@components/tree-select/ActivityStatusTreeSelect"
import { DefaultListItem } from "@components/default-list/list-item"
import { ActivityTypeTreeSelect } from "@components/tree-select/ActivityTypeTreeSelect"

interface Filter {
  seasonIds?: number[]
  productionIds?: number[]
  activityTypeNodes?: string[]
  locationTreeNodes?: string[]
  activityStatusTreeNodes?: string[]
  note?: string
}

export interface Search extends Filter {
  reminderDetailOpened?: boolean
  reminderId?: number
  sorting?: ColumnSort[]
  dateRange?: [string | null, string | null]
}

export const Route = createFileRoute("/app/art/activities/")({
  component: ActivitiesPage,
  validateSearch: (search: Search) => ({
    ...search,
    reminderDetailOpened: search.reminderDetailOpened,
    reminderId: search.reminderId,
    sorting: search.sorting ?? [
      { id: "date", desc: false },
      { id: "timePeriod", desc: false },
    ],
    dateRange: search.dateRange ?? [
      new Date().toISOString().substring(0, 10),
      null,
    ],
  }),
})

function ActivitiesPage() {
  const search = Route.useSearch()
  const {
    activityTypeNodes,
    productionIds,
    seasonIds,
    activityStatusTreeNodes,
    locationTreeNodes,
  } = search
  const [showFilters, setShowFilters] = useUserLocalStorage<boolean>({
    key: "activities-show-filters",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(showFilters)

  const [storedActivityTypeNodes, setStoredActivityTypeNodes] =
    useUserLocalStorage<string[]>({
      key: "activities-activity-type-filter",
      defaultValue: [],
    })
  React.useEffect(() => {
    updateSearch({
      activityTypeNodes: activityTypeNodes || storedActivityTypeNodes || [],
      productionIds: productionIds || [],
    })
  }, [])

  const { data: productions } = useSuspenseQueryGet(
    "/api/Production/GetProductionsInSeasons",
    {
      seasonIds: seasonIds ?? [],
    }
  )
  const { xs } = useBreakpoints()
  const { t } = useTranslation("app_server")

  const navigate = Route.useNavigate()

  const navigatToActivityId = (activityId: number) =>
    navigate({
      to: "/app/art/activities/$id",
      params: { id: activityId.toString() },
      search: { tab: "start" },
    })

  function updateSearch(input: Partial<Search>) {
    navigate({
      search: {
        ...search,
        ...input,
      },
      replace: true,
    })
  }
  const { extendedColumns, defaultColumnVisibility } = useActivityTableColumns()

  const sortingState: [
    SortingState,
    React.Dispatch<React.SetStateAction<SortingState>>,
  ] = [
    search.sorting,
    (updater) =>
      typeof updater === "function"
        ? navigate({
            to: "/app/art/activities",
            search: { ...search, sorting: updater(search.sorting) },
          })
        : navigate({
            to: "/app/art/activities",
            search: { ...search, sorting: updater },
          }),
  ]

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedActivities,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
      sorting: sortingState[0],
      start: search.dateRange?.[0] ?? null,
      end: search.dateRange?.[1] ?? null,
      activityTypeNodes: search.activityTypeNodes ?? [],
      locationTreeNodes: search.locationTreeNodes ?? [],
      activityStatusTreeNodes: search.activityStatusTreeNodes ?? [],
      productionIds: search.productionIds ?? [],
    }),
    queryOptions
  )

  const inputProps = { w: xs ? "100%" : 300 }

  return (
    <Page
      buttonGroupProps={{
        children: (
          <ResponsiveFilterGroup.Button
            filters={{
              productionIds,
              dateRange: search.dateRange,
              activityTypeNodes,
              seasonIds,
              activityStatusTreeNodes,
              locationTreeNodes,
              note: search.note,
            }}
            visible={showFilters}
            onChange={() => setShowFilters(!showFilters)}
          />
        ),
      }}
    >
      <Stack gap={"xs"} h={"100%"}>
        <Group gap={"xs"}>
          <MultiSelect
            {...inputProps}
            key={"production-filter"}
            pointer
            clearable
            placeholder={
              productionIds && productionIds.length > 0
                ? ""
                : t("FilterProductions")
            }
            label={t("Productions")}
            searchable
            data={productions.map((v) => ({
              value: v.productionId.toString(),
              label: v.title,
            }))}
            value={productionIds?.map((v) => v.toString())}
            onChange={(value) =>
              updateSearch({ productionIds: value?.map((v) => parseInt(v)) })
            }
          />
          <ResponsiveFilterGroup
            gap={"xs"}
            visible={showFilters}
            setVisible={setShowFilters}
            filters={{
              productionIds,
              dateRange: search.dateRange,
              activityTypeNodes,
              seasonIds,
              activityStatusTreeNodes,
              locationTreeNodes,
              note: search.note,
            }}
            onReset={() => {
              setStoredActivityTypeNodes([])
              updateSearch({
                productionIds: [],
                dateRange: undefined,
                activityTypeNodes: [],
                seasonIds: undefined,
                activityStatusTreeNodes: [],
                locationTreeNodes: [],
                note: undefined,
              })
            }}
          >
            <DateRangePickerInputWithSeasonPresets
              {...inputProps}
              label={t("TimePeriod")}
              value={search.dateRange}
              onDropdownClose={() => {}}
              onChange={(dateRange) => updateSearch({ dateRange })}
              allowSingleDateInRange
              clearable
            />
            <ActivityTypeTreeSelect
              {...inputProps}
              label={t("ActivityTypes")}
              placeholder={t("FilterActivityTypes")}
              value={search.activityTypeNodes}
              onChange={(v) => {
                setStoredActivityTypeNodes(v)
                updateSearch({ activityTypeNodes: v })
              }}
            />

            <LocationTreeSelect
              {...inputProps}
              value={locationTreeNodes}
              onChange={(v) => updateSearch({ locationTreeNodes: v })}
            />
            <ActivityStatusTreeSelect
              {...inputProps}
              value={activityStatusTreeNodes}
              onChange={(v) => updateSearch({ activityStatusTreeNodes: v })}
            />
            <TextInput
              {...inputProps}
              label={t("Note")}
              value={search.note}
              onChange={(e) => updateSearch({ note: e.target.value })}
            />
          </ResponsiveFilterGroup>
        </Group>
        {xs ? (
          <DefaultInfiniteList
            h={"100%"}
            queryResult={queryResult}
            renderItem={({ entity }) => (
              <DefaultListItem w={"100%"} key={entity.activityId}>
                <ActivityListItemCard
                  item={entity}
                  onClickItem={() => navigatToActivityId(entity.activityId)}
                />
              </DefaultListItem>
            )}
          />
        ) : (
          <InfiniteScrollingTable
            id="activities-table"
            infiniteQueryResult={queryResult}
            sortingState={sortingState}
            defaultColumnVisibility={defaultColumnVisibility}
            columnDefinitions={extendedColumns}
            getRowProps={(row) => ({
              onClick: () => navigatToActivityId(row.original.activityId),
            })}
          />
        )}
      </Stack>
    </Page>
  )
}
